<script setup lang="ts">
/**
 * SidePanel · 右栏（全局常驻，310px）
 * ------------------------------------------------------------
 * 信息架构（2026-11 重排）：
 *   核心三区 —— ① AI 状态进展（3 秒判断工作方向，可就地介入：取消/改问题）
 *              ② 当前意图（业务位置 + 本次提问范围）
 *              ③ 待执行操作清单（AI 实时作业 + 本页可做）
 *   会话规则 —— 仅聊天页：本人用自然语言约定本会话可执行/不可执行的原则，
 *              提交后并入该会话后续回答的系统提示（服务端 chat.session_rule）。
 *   更多     —— 次要信息统一收纳为跳转链接：背景与记忆 / AI 的理解与思考 /
 *              历史操作用 UDrawer 二级承载，待我处理进 /tasks 二级页面。
 * 数据仍读 /api/assistant/panel（4s 轮询）；没有落库的内容保持空白，不显示演示句子。
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { assistantApi, type AssistantPanel, type PanelAction } from '@/data/api/assistant'
import { chatApi, type ChatSessionRule } from '@/data/api/chat'
import { useScopeStore } from '@/domain/scope/store'
import { usePageContextStore, type PageAction } from '@/domain/page-context'
import { useSessionStore } from '@/domain/session/store'
import { on } from '@/shared/events'
import { addMemory, removeMemory } from '@/shared/memory'
import UDrawer from '@/design/ui/UDrawer.vue'

const scope = useScopeStore()
const pageCtx = usePageContextStore()
const page = computed(() => pageCtx.current)
const router = useRouter()
const route = useRoute()
const sess = useSessionStore()
const draft = ref('')
const memBusy = ref('')
const memFormError = ref('')
const loadError = ref('')
const loading = ref(false)
const loaded = ref(false)
let ticket = 0
let timer: ReturnType<typeof setInterval> | undefined

const empty: AssistantPanel = {
  background: { orgName: '—', legal: '—', period: '—', role: '—', perm: '—', exec: false, appTotal: null, appAvailable: '—', note: null, error: null },
  memories: { items: [], error: null },
  scope: { context: null, error: null },
  actions: { upcoming: [], todos: { items: [], total: 0 }, history: [], error: null },
  thinking: { items: [], error: null },
}
const panel = ref<AssistantPanel>(empty)
const ctx = computed(() => panel.value.scope.context)
const background = computed(() => panel.value.background)
const todos = computed(() => panel.value.actions.todos)
const actBusy = ref('')
const actError = ref('')
/* 二级承载：同一时刻只开一个抽屉 */
const drawer = ref<'' | 'scope' | 'background' | 'thinking' | 'history' | 'rules'>('')

const STATUS_LABEL: Record<string, string> = {
  pending: '待执行',
  running: '执行中',
  done: '已完成',
  failed: '失败',
  skipped: '跳过',
}

function fmtTime(at: string | null) {
  if (!at) return ''
  const d = new Date(at)
  if (Number.isNaN(d.getTime())) return ''
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`
}

async function load() {
  const n = ++ticket
  loading.value = true
  try {
    const data = await assistantApi.panel(scope.current?.id || undefined)
    if (n !== ticket) return
    panel.value = data
    loadError.value = ''
    loaded.value = true
  } catch (error) {
    if (n !== ticket) return
    loadError.value = error instanceof Error ? error.message : '右栏没有读出来，请重试'
  } finally {
    if (n === ticket) loading.value = false
  }
}

async function addMem() {
  const text = draft.value.trim()
  if (!text || memBusy.value) return
  memBusy.value = 'add'
  memFormError.value = ''
  try {
    await addMemory(text)
    draft.value = ''
  } catch (error) {
    memFormError.value = error instanceof Error ? error.message : '没有记下，请重试'
  } finally { memBusy.value = '' }
}

async function delMem(id: string) {
  if (memBusy.value) return
  memBusy.value = id
  memFormError.value = ''
  try { await removeMemory(id) }
  catch (error) { memFormError.value = error instanceof Error ? error.message : '没有删除，请重试' }
  finally { memBusy.value = '' }
}

function openAction(step: PanelAction) {
  if (!step.route) return
  void router.push({ path: step.route.path, query: step.route.query })
}

/* AI 作业介入：聊天作业 id 形如 chat-<messageId>；取消走既有 cancel 接口，
 * 改问题跳转会话页并把消息 id 带在 edit 参数里，由会话页取消后回填输入框。 */
function chatJobId(s: PanelAction) {
  return s.kind === 'chat' ? s.id.replace(/^chat-/, '') : ''
}
async function cancelJob(s: PanelAction) {
  const id = chatJobId(s)
  if (!id || actBusy.value) return
  actBusy.value = s.id
  actError.value = ''
  try {
    await chatApi.cancel(id)
    await load()
  } catch (error) {
    actError.value = error instanceof Error ? error.message : '取消没有确认，请重试'
  } finally { actBusy.value = '' }
}
function editJob(s: PanelAction) {
  const id = chatJobId(s)
  if (!id || !s.route) return
  void router.push({ path: s.route.path, query: { ...s.route.query, edit: id } })
}
function openTodos() {
  void router.push({ path: '/tasks' })
}

function openPageAction(a: PageAction) {
  if (!a.route) return
  void router.push({ path: a.route.path, query: a.route.query })
}

/* ---------- ① AI 状态进展：3 秒判断区 ----------
 * running > 排队 > 最近一次失败 > 空闲；失败态从最近一条历史操作推导，
 * 让人不用翻记录就能发现异常并就地介入（重试入口在历史抽屉）。 */
const runningJob = computed(() => panel.value.actions.upcoming.find(s => s.status === 'running') || null)
const pendingJobs = computed(() => panel.value.actions.upcoming.filter(s => s.status === 'pending'))
const lastFailed = computed(() => panel.value.actions.history.find(s => s.status === 'failed') || null)
const isChat = computed(() => route.path === '/chat' && !route.query.discussion)
const chatMsgCount = computed(() => sess.current?.messages.length ?? 0)
const st = computed<{ kind: 'run' | 'queue' | 'fail' | 'idle'; label: string; sub: string }>(() => {
  if (runningJob.value) {
    const queued = pendingJobs.value.length
    return { kind: 'run', label: 'AI 正在为你工作', sub: queued ? `另有 ${queued} 项排队` : '回答生成中，可实时查看' }
  }
  if (pendingJobs.value.length) return { kind: 'queue', label: `${pendingJobs.value.length} 项操作排队中`, sub: '会按顺序处理，可取消或改问题' }
  if (lastFailed.value) return { kind: 'fail', label: '最近一次操作失败', sub: lastFailed.value.title }
  return { kind: 'idle', label: 'AI 空闲', sub: isChat.value ? (chatMsgCount.value ? `本会话已进行 ${chatMsgCount.value} 轮` : '随时可以提问') : '当前没有进行中的操作' }
})
/* 状态卡聚焦的当前作业：执行中优先，其次队首 */
const stJob = computed(() => runningJob.value || pendingJobs.value[0] || null)

/* ---------- ② 当前意图：范围详情收入抽屉 ---------- */
const scopeHasMore = computed(() => !!(page.value.summary || ctx.value?.objectNo || ctx.value?.relatedDocs || ctx.value?.org || ctx.value?.period || ctx.value?.authority))

/* ---------- ④ 本会话规则（仅聊天页已落库会话） ---------- */
const chatSessionId = computed(() => (isChat.value && sess.current?.persisted ? sess.current.id : ''))
const rules = ref<ChatSessionRule[]>([])
const ruleDraft = ref('')
const ruleBusy = ref('')
const ruleError = ref('')
const rulesShown = computed(() => rules.value.slice(0, 3))
let ruleTicket = 0
async function loadRules() {
  const sid = chatSessionId.value
  const n = ++ruleTicket
  rules.value = []
  ruleError.value = ''
  if (!sid) return
  try {
    const items = await chatApi.rules(sid)
    if (n !== ruleTicket) return
    rules.value = items
  } catch (error) {
    if (n !== ruleTicket) return
    ruleError.value = error instanceof Error ? error.message : '规则没有读出来，请重试'
  }
}
async function addRule() {
  const text = ruleDraft.value.trim()
  if (!text || ruleBusy.value || !chatSessionId.value) return
  ruleBusy.value = 'add'
  ruleError.value = ''
  try {
    const row = await chatApi.addRule(chatSessionId.value, text)
    rules.value = [...rules.value, row]
    ruleDraft.value = ''
  } catch (error) {
    ruleError.value = error instanceof Error ? error.message : '规则没有保存，请重试'
  } finally { ruleBusy.value = '' }
}
async function delRule(r: ChatSessionRule) {
  if (ruleBusy.value || !chatSessionId.value) return
  ruleBusy.value = r.id
  ruleError.value = ''
  try {
    await chatApi.removeRule(chatSessionId.value, r.id)
    rules.value = rules.value.filter(x => x.id !== r.id)
  } catch (error) {
    ruleError.value = error instanceof Error ? error.message : '没有删除，请重试'
  } finally { ruleBusy.value = '' }
}
watch(chatSessionId, () => { void loadRules() }, { immediate: true })

/* 思考条目数（页面视角 + 服务端记录），用于「更多」行摘要 */
const thinkCount = computed(() => (page.value.insights?.length ?? 0) + panel.value.thinking.items.length)

watch(() => scope.current?.id || '', () => { void load() }, { immediate: true })
const offMem = on('memory:changed', () => { void load() })
const offRefresh = on('assistant:refresh', () => { void load() })
timer = setInterval(() => { void load() }, 4000)
onBeforeUnmount(() => { offMem(); offRefresh(); if (timer) clearInterval(timer); ticket += 1 })
</script>

<template>
  <aside class="side" data-side="panel">
    <p v-if="loadError" class="side-err">{{ loadError }} <button type="button" @click="load">重试</button></p>

    <!-- ================ ① AI 状态进展（3 秒判断区） ================ -->
    <section class="st" :class="st.kind" data-side="status">
      <div class="st-top">
        <i class="st-dot" aria-hidden="true" />
        <b>{{ st.label }}</b>
      </div>
      <p class="st-sub" :title="st.sub">{{ st.sub }}</p>
      <!-- 当前作业：就地介入（取消 / 改问题） -->
      <div v-if="stJob" class="st-job">
        <div class="st-job-title" :title="stJob.title">
          {{ stJob.title }}
          <span class="op-status" :class="stJob.status">{{ STATUS_LABEL[stJob.status] }}</span>
        </div>
        <div v-if="stJob.detail" class="st-job-detail">{{ stJob.detail }}</div>
        <div class="st-acts">
          <button v-if="stJob.status === 'pending'" type="button" :disabled="!!actBusy" title="取消该作业，把问题带回输入框重新编辑" @click="editJob(stJob)">改问题</button>
          <button type="button" class="cancel" :disabled="!!actBusy" title="终止这次回答，立即生效" @click="cancelJob(stJob)">{{ actBusy === stJob.id ? '取消中…' : '取消' }}</button>
        </div>
      </div>
      <button v-if="st.kind === 'fail'" type="button" class="lk" title="查看失败详情与重试入口" @click="drawer = 'history'">查看失败详情 ›</button>
      <p v-if="actError" class="side-err">{{ actError }}</p>
    </section>

    <!-- ================ ② 当前意图 ================ -->
    <section class="side-sec" data-side="intent">
      <div class="side-ttl"><span>当前意图</span><span class="ln" /></div>
      <div class="intent-card">
        <div class="intent-line">
          <b>{{ page.line }}</b>
          <span class="sep" aria-hidden="true">·</span>
          <span>{{ page.node }}</span>
        </div>
        <div v-if="page.object" class="intent-obj" :title="page.object">{{ page.object }}</div>
        <div v-if="ctx?.scopeNote" class="intent-note" :title="ctx.scopeNote">“{{ ctx.scopeNote }}”</div>
        <button v-if="scopeHasMore" type="button" class="lk" title="页面说明、对象编号、涉及单据、责任组织等完整范围" @click="drawer = 'scope'">范围详情 ›</button>
      </div>
      <p v-if="panel.scope.error" class="side-err">{{ panel.scope.error }}</p>
    </section>

    <!-- ================ ③ 待执行操作 ================ -->
    <section class="side-sec" data-side="ops">
      <div class="side-ttl">
        <span>待执行操作</span>
        <span class="tag">{{ panel.actions.upcoming.length + (page.actions?.length ?? 0) }} 项</span>
        <span class="ln" />
      </div>
      <p v-if="panel.actions.error" class="side-err">{{ panel.actions.error }}</p>

      <!-- AI 实时作业：不介入就直接执行，可取消或改问题重发 -->
      <div v-for="s in panel.actions.upcoming" :key="s.id" class="op-step" :class="[s.status, { hit: !!s.route }]"
           role="button" tabindex="0" :title="s.route ? '点击进入相关页面' : undefined" @click="openAction(s)" @keydown.enter="openAction(s)">
        <i class="op-bullet" :class="s.status === 'running' ? 'accent' : 'info'" />
        <div class="op-body">
          <div class="op-title">
            {{ s.title }}
            <span class="op-status" :class="s.status">{{ STATUS_LABEL[s.status] }}</span>
          </div>
          <div v-if="s.detail" class="op-detail">{{ s.detail }}</div>
          <div class="op-acts" @click.stop>
            <button v-if="s.status === 'pending'" type="button" :disabled="!!actBusy" title="取消该作业，把问题带回输入框重新编辑" @click="editJob(s)">改问题</button>
            <button type="button" class="cancel" :disabled="!!actBusy" title="终止这次回答，立即生效" @click="cancelJob(s)">{{ actBusy === s.id ? '取消中…' : '取消' }}</button>
          </div>
        </div>
        <div v-if="s.status === 'running'" class="op-spin"><i /><i /><i /></div>
      </div>

      <!-- 本页可做：当前页面登记的操作 -->
      <button v-for="a in page.actions ?? []" :key="a.id" type="button" class="op-step page-op" :class="{ hit: !!a.route }"
              :title="a.detail || a.title" @click="openPageAction(a)">
        <i class="op-bullet teal" />
        <div class="op-body">
          <div class="op-title">{{ a.title }}</div>
        </div>
      </button>

      <p v-if="loaded && !panel.actions.upcoming.length && !page.actions?.length && !panel.actions.error" class="op-none">
        AI 当前没有排队或执行中的操作
      </p>
    </section>

    <!-- ================ ④ 本会话规则（仅聊天页） ================ -->
    <section v-if="isChat" class="side-sec" data-side="rules">
      <div class="side-ttl">
        <span>本会话规则</span>
        <span v-if="rules.length" class="tag on" :title="`已生效 ${rules.length} 条，后续回答自动遵循`">{{ rules.length }} 条生效</span>
        <span class="ln" />
      </div>
      <template v-if="chatSessionId">
        <div v-for="r in rulesShown" :key="r.id" class="rule-it">
          <span class="rule-text" :title="r.text">{{ r.text }}</span>
          <button type="button" class="rule-del" :disabled="!!ruleBusy" title="删除这条规则，后续回答不再遵循" @click="delRule(r)">×</button>
        </div>
        <button v-if="rules.length > 3" type="button" class="lk" title="查看并管理本会话全部规则" @click="drawer = 'rules'">管理全部 {{ rules.length }} 条 ›</button>
        <form class="rule-form" @submit.prevent="addRule">
          <input v-model="ruleDraft" maxlength="500" :disabled="!!ruleBusy"
                 placeholder="如：只给结论，不要贴代码"
                 title="用一句话写下希望 AI 在本会话遵守的原则，回车即生效" aria-label="新增本会话规则" />
          <button type="submit" :disabled="!!ruleBusy || !ruleDraft.trim()" title="保存为本会话规则，下一条回答起生效">{{ ruleBusy === 'add' ? '保存中…' : '添加' }}</button>
        </form>
        <p v-if="ruleError" class="side-err">{{ ruleError }}</p>
        <p class="rule-tip">规则只约束本会话回答的表达方式，不改变数据与权限，最多 20 条。</p>
      </template>
      <p v-else class="sec-empty">发送首条消息后，可为本会话约定 AI 的行为规则。</p>
    </section>

    <!-- ================ ⑤ 更多（次要信息 → 二级承载） ================ -->
    <section class="side-sec" data-side="more">
      <div class="side-ttl"><span>更多</span><span class="ln" /></div>
      <button type="button" class="more-row" title="数据域、法人主体、我的角色、执行权限与 AI 长期记忆" @click="drawer = 'background'">
        <span class="more-lb">背景信息与记忆</span>
        <span class="more-meta">{{ panel.memories.items.length }} 条记忆</span>
        <span class="chev" aria-hidden="true">›</span>
      </button>
      <button type="button" class="more-row" title="AI 对当前页面的理解、数据来源与判断记录" @click="drawer = 'thinking'">
        <span class="more-lb">AI 的理解与思考</span>
        <span class="more-meta">{{ thinkCount }} 条</span>
        <span class="chev" aria-hidden="true">›</span>
      </button>
      <button type="button" class="more-row" title="已完成与失败的操作记录，失败可查看原因" @click="drawer = 'history'">
        <span class="more-lb">历史操作记录</span>
        <span class="more-meta">{{ panel.actions.history.length }} 条</span>
        <span class="chev" aria-hidden="true">›</span>
      </button>
      <button type="button" class="more-row" title="待决策、待审批、待执行，进入任务页处理" @click="openTodos">
        <span class="more-lb">待我处理</span>
        <span class="more-meta">{{ todos.total }} 项</span>
        <span class="chev" aria-hidden="true">›</span>
      </button>
    </section>

    <!-- ================ 二级抽屉：范围详情 ================ -->
    <UDrawer :open="drawer === 'scope'" title="范围详情" width="400px" @close="drawer = ''">
      <div class="d-card">
        <div class="d-row"><span>业务主线</span><b>{{ page.line }}</b></div>
        <div class="d-row"><span>当前节点</span><b>{{ page.node }}</b></div>
        <div v-if="page.object" class="d-row"><span>查看对象</span><b class="mono">{{ page.object }}</b></div>
        <div v-if="page.summary" class="d-row col"><span>页面说明</span><p>{{ page.summary }}</p></div>
        <template v-if="ctx">
          <div v-if="ctx.scopeNote" class="d-row col"><span>本次提问</span><p>{{ ctx.scopeNote }}</p></div>
          <div v-if="ctx.objectNo" class="d-row"><span>对象编号</span><b class="mono">{{ ctx.objectNo }}</b></div>
          <div v-if="ctx.relatedDocs" class="d-row"><span>涉及单据</span><b class="mono">{{ ctx.relatedDocs }}</b></div>
          <div v-if="ctx.org" class="d-row"><span>责任组织</span><b>{{ ctx.org }}</b></div>
          <div v-if="ctx.period" class="d-row"><span>会计期间</span><b>{{ ctx.period }}</b></div>
          <div v-if="ctx.authority" class="d-row"><span>数据权限</span><b>{{ ctx.authority }}</b></div>
        </template>
      </div>
    </UDrawer>

    <!-- ================ 二级抽屉：背景信息与 AI 长期记忆 ================ -->
    <UDrawer :open="drawer === 'background'" title="背景信息与 AI 长期记忆" width="400px" @close="drawer = ''">
      <p v-if="background.error" class="side-err">{{ background.error }}</p>
      <div class="d-card">
        <div class="d-row"><span>数据域</span><b>{{ loaded ? background.orgName : '读取中' }}</b></div>
        <div class="d-row"><span>法人主体</span><b>{{ loaded ? background.legal : '—' }}</b></div>
        <div class="d-row"><span>会计期间</span><b>{{ loaded ? background.period : '—' }}</b></div>
        <div class="d-row"><span>我的角色</span><b>{{ loaded ? background.role : '—' }}</b></div>
        <div class="d-row"><span>库内应用</span><b>{{ loaded ? (background.appTotal ?? '—') : '—' }}</b></div>
        <div class="d-row"><span>可用应用</span><b>{{ loaded ? background.appAvailable : '—' }}</b></div>
        <div class="d-row" :class="{ warn: loaded && !background.exec }"><span>执行权限</span><b>{{ loaded ? background.perm : '—' }}</b></div>
        <p v-if="background.note" class="d-note">{{ background.note }}</p>
      </div>

      <div class="mem" data-side="memory">
        <div class="mt">◆ AI 长期记忆</div>
        <form class="mem-form" @submit.prevent="addMem">
          <input v-model="draft" maxlength="500" placeholder="记下一条偏好或口径" :disabled="!!memBusy"
                 title="跨会话长期生效，保存在你的账号下" aria-label="长期记忆" />
          <button type="submit" :disabled="!!memBusy || !draft.trim()" title="保存为长期记忆">记下</button>
        </form>
        <p v-if="memFormError || panel.memories.error" class="side-err">{{ memFormError || panel.memories.error }}</p>
        <div v-for="m in panel.memories.items" :key="m.id" class="mem-it">
          <span>{{ m.text }}</span>
          <button type="button" :disabled="!!memBusy" title="删除这条记忆" @click="delMem(m.id)">删除</button>
        </div>
        <p v-if="loaded && !panel.memories.items.length && !panel.memories.error" class="mem-empty">还没有长期记忆。写下一句希望助手记住的偏好，会保存在你的账号下。</p>
      </div>
    </UDrawer>

    <!-- ================ 二级抽屉：AI 的理解与思考 ================ -->
    <UDrawer :open="drawer === 'thinking'" title="AI 的理解与思考" width="420px" @close="drawer = ''">
      <div v-for="(t, i) in page.insights ?? []" :key="'ins-' + i" class="think-item" :class="{ latest: i === 0 && !panel.thinking.items.length }">
        <span class="think-tag 理解">理解</span>
        <span class="think-text">{{ t }}</span>
      </div>
      <p v-if="panel.thinking.error" class="side-err">{{ panel.thinking.error }}</p>
      <template v-else-if="panel.thinking.items.length">
        <div v-for="(t, idx) in panel.thinking.items" :key="t.id" class="think-item" :class="{ latest: idx === 0 }">
          <span class="think-tag" :class="t.tag">{{ t.tag }}</span>
          <span class="think-text">{{ t.text }}</span>
          <span class="think-time">{{ fmtTime(t.at) }}</span>
        </div>
      </template>
      <div v-if="!page.insights?.length && !panel.thinking.items.length && !panel.thinking.error" class="think-empty">
        本页尚未登记 AI 视角说明。<br>
        模型的内部推理不保存，回答失败时原因会记在这里。
      </div>
    </UDrawer>

    <!-- ================ 二级抽屉：历史操作记录 ================ -->
    <UDrawer :open="drawer === 'history'" title="历史操作记录" width="420px" @close="drawer = ''">
      <button v-for="s in panel.actions.history" :key="s.id" type="button" class="op-step history" :class="[s.status, { hit: !!s.route }]"
              :title="s.route ? '点击进入相关页面' : undefined" @click="openAction(s); drawer = ''">
        <i class="op-bullet info" />
        <div class="op-body">
          <div class="op-title">
            {{ s.title }}
            <span class="op-status" :class="s.status">{{ STATUS_LABEL[s.status] }}</span>
          </div>
          <div v-if="s.detail" class="op-detail">{{ s.detail }}</div>
          <div v-if="fmtTime(s.at)" class="op-time"><span>{{ fmtTime(s.at) }}</span></div>
        </div>
      </button>
      <div v-if="!panel.actions.history.length" class="think-empty">还没有历史操作记录。</div>
    </UDrawer>

    <!-- ================ 二级抽屉：本会话规则管理 ================ -->
    <UDrawer :open="drawer === 'rules'" title="本会话规则" width="400px" @close="drawer = ''">
      <p class="d-note">以下规则在当前会话后续回答中自动生效，只约束回答的表达方式，不改变数据与权限。</p>
      <div v-for="r in rules" :key="r.id" class="rule-it lg">
        <span class="rule-text">{{ r.text }}</span>
        <button type="button" class="rule-del" :disabled="!!ruleBusy" title="删除这条规则，后续回答不再遵循" @click="delRule(r)">×</button>
      </div>
      <p v-if="!rules.length && !ruleError" class="mem-empty">还没有规则。在右栏「本会话规则」输入一句话即可添加。</p>
      <p v-if="ruleError" class="side-err">{{ ruleError }}</p>
    </UDrawer>
  </aside>
</template>

<style scoped>
.side {
  border-left: 1px solid var(--line);
  background: var(--surface-2);
  overflow-y: auto;
  overflow-x: hidden;
  padding: 14px 12px 24px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* ===== 分节标题 ===== */
.side-ttl {
  font-size: 10.5px;
  font-weight: 600;
  color: var(--ink-4);
  letter-spacing: .7px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  text-transform: uppercase;
}
.side-ttl .ln { flex: 1; height: 1px; background: var(--line); }
.side-ttl .tag {
  font-size: 9px;
  font-weight: 500;
  letter-spacing: .04em;
  padding: 1px 7px;
  border-radius: 99px;
  background: var(--surface);
  border: 1px solid var(--line);
  color: var(--ink-4);
  text-transform: none;
}
.side-ttl .tag.on { background: var(--accent-soft); border-color: var(--accent-line); color: var(--accent); }

/* ===== ① AI 状态卡 ===== */
.st {
  border: 1px solid var(--line-soft);
  border-radius: var(--r-m);
  background: var(--surface);
  padding: 10px 12px 11px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.st-top { display: flex; align-items: center; gap: 8px; }
.st-top b { font-size: 13px; font-weight: 700; color: var(--ink); }
.st-dot { flex: none; width: 9px; height: 9px; border-radius: 50%; background: var(--ink-4); }
.st.run .st-dot { background: var(--accent); box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 25%, transparent); animation: pulse 1.4s ease-in-out infinite; }
.st.queue .st-dot { background: var(--amber, #ca8a04); }
.st.fail .st-dot { background: var(--danger, #b42318); }
.st.idle .st-dot { background: var(--teal, #16a34a); }
.st-sub { margin: 0; font-size: 11.5px; color: var(--ink-3); line-height: 1.5; }
.st.run { border-color: var(--accent-line); background: linear-gradient(160deg, #F7F5FF, #EFF3FF); }
.st.fail { border-color: color-mix(in srgb, var(--danger, #b42318) 35%, transparent); }
.st-job { margin-top: 6px; border-top: 1px dashed var(--line-soft); padding-top: 8px; }
.st-job-title { display: flex; align-items: center; gap: 6px; font-size: 12.5px; font-weight: 600; color: var(--ink); }
.st-job-detail { margin-top: 3px; font-size: 11.5px; color: var(--ink-3); line-height: 1.5; word-break: break-word; }
.st-acts { margin-top: 6px; display: flex; gap: 12px; }
.st-acts button { border: 0; background: transparent; padding: 0; color: var(--accent); cursor: pointer; font: inherit; font-size: 11.5px; }
.st-acts button:hover { text-decoration: underline; }
.st-acts button.cancel { color: var(--ink-3); }
.st-acts button.cancel:hover { color: var(--danger, #b42318); }
.st-acts button:disabled { opacity: .45; cursor: not-allowed; text-decoration: none; }

/* ===== 链接（跳转二级承载） ===== */
.lk {
  align-self: flex-start;
  margin-top: 6px;
  border: 0; background: transparent; padding: 0;
  color: var(--accent); cursor: pointer; font: inherit; font-size: 11.5px;
}
.lk:hover { text-decoration: underline; }

/* ===== ② 当前意图 ===== */
.intent-card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-m);
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.intent-line { display: flex; align-items: baseline; gap: 6px; font-size: 13.5px; color: var(--ink-2); }
.intent-line b { font-weight: 700; color: var(--ink); }
.intent-line .sep { color: var(--ink-4); }
.intent-obj {
  font-family: var(--mono, ui-monospace, monospace);
  font-size: 11.5px; color: var(--accent);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.intent-note {
  font-size: 11.5px; color: var(--ink-3); line-height: 1.55;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}

/* ===== ③ 待执行操作 ===== */
.op-step {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 8px 10px;
  margin-bottom: 5px;
  background: var(--surface);
  border: 1px solid var(--line-soft);
  border-radius: var(--r-s);
  position: relative;
  animation: opIn .25s ease both;
}
@keyframes opIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
.op-step.running { border-color: var(--accent-line); background: linear-gradient(160deg, #F7F5FF, #EFF3FF); }
.op-step.failed { border-color: color-mix(in srgb, var(--danger, #b42318) 35%, transparent); }
.op-step.history { padding: 6px 10px; }
.op-step.history.done { opacity: .85; }
button.op-step { width: 100%; font: inherit; color: inherit; text-align: left; cursor: default; }
button.op-step.hit { cursor: pointer; }
button.op-step.hit:hover { border-color: var(--accent-line); box-shadow: 0 1px 4px #0000000f; }
div.op-step.hit { cursor: pointer; }
div.op-step.hit:hover { border-color: var(--accent-line); box-shadow: 0 1px 4px #0000000f; }
div.op-step:focus-visible { outline: 2px solid var(--accent-line); outline-offset: 1px; }

.op-bullet { flex: none; width: 10px; height: 10px; margin-top: 4px; border-radius: 50%; background: var(--ink-4); }
.op-bullet.info { background: var(--ink-4); }
.op-bullet.accent { background: var(--accent); }
.op-bullet.teal { background: var(--teal, #16a34a); }
.op-step.running .op-bullet { box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 30%, transparent); animation: pulse 1.4s ease-in-out infinite; }
.op-step.failed .op-bullet { background: var(--danger, #b42318); }
.op-step.history .op-bullet { opacity: .5; }
@keyframes pulse {
  0%, 100% { box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 30%, transparent); }
  50% { box-shadow: 0 0 0 6px color-mix(in srgb, var(--accent) 10%, transparent); }
}

.op-body { flex: 1; min-width: 0; }
.op-title { display: flex; align-items: center; gap: 6px; font-size: 12.5px; font-weight: 600; color: var(--ink); }
.op-status {
  font-size: 10px; font-weight: 500;
  padding: 1px 7px; border-radius: 99px;
  background: var(--surface-3); color: var(--ink-4);
  margin-left: auto; flex: none;
}
.op-status.running { background: var(--accent-soft); color: var(--accent); }
.op-status.pending { background: #FEF3C7; color: #B45309; }
.op-status.done { background: var(--teal-soft, #dcfce7); color: var(--teal, #16a34a); }
.op-status.failed { background: color-mix(in srgb, var(--danger, #b42318) 20%, transparent); color: var(--danger, #b42318); }
.op-status.skipped { background: var(--surface-3); color: var(--ink-4); }
.op-detail { margin-top: 3px; font-size: 11.5px; color: var(--ink-3); line-height: 1.5; word-break: break-word; }
.op-time { margin-top: 3px; font-size: 10px; color: var(--ink-4); display: flex; gap: 4px; }
.op-acts { margin-top: 6px; display: flex; gap: 12px; }
.op-acts button { border: 0; background: transparent; padding: 0; color: var(--accent); cursor: pointer; font: inherit; font-size: 11.5px; }
.op-acts button:hover { text-decoration: underline; }
.op-acts button.cancel { color: var(--ink-3); }
.op-acts button.cancel:hover { color: var(--danger, #b42318); }
.op-acts button:disabled { opacity: .45; cursor: not-allowed; text-decoration: none; }
.op-none { margin: 2px 2px 4px; font-size: 11.5px; color: var(--ink-4); }
.op-spin { flex: none; display: flex; gap: 2px; margin-top: 6px; }
.op-spin i { width: 4px; height: 4px; border-radius: 50%; background: var(--accent); animation: blink 1s steps(1) infinite; }
.op-spin i:nth-child(2) { animation-delay: .2s; }
.op-spin i:nth-child(3) { animation-delay: .4s; }
@keyframes blink { 50% { opacity: .2; } }

/* ===== ④ 本会话规则 ===== */
.rule-it {
  display: flex; align-items: flex-start; gap: 8px;
  padding: 7px 10px; margin-bottom: 5px;
  background: var(--surface);
  border: 1px solid var(--line-soft);
  border-radius: var(--r-s);
  font-size: 12px; line-height: 1.5; color: var(--ink-2);
}
.rule-it:hover { border-color: var(--accent-line); }
.rule-text { flex: 1; min-width: 0; word-break: break-word; }
.rule-del {
  flex: none; width: 18px; height: 18px; line-height: 1;
  border: 0; border-radius: 5px; background: transparent;
  color: var(--ink-4); cursor: pointer; font-size: 13px;
}
.rule-del:hover { background: var(--surface-3); color: var(--danger, #b42318); }
.rule-del:disabled { opacity: .45; cursor: not-allowed; }
.rule-form { display: flex; gap: 6px; margin-top: 2px; }
.rule-form input {
  flex: 1; min-width: 0; border: 1px solid var(--line); border-radius: var(--r-s);
  background: var(--surface); color: var(--ink); font: inherit; font-size: 12px; padding: 6px 9px;
}
.rule-form input:focus { outline: 2px solid var(--accent-line); outline-offset: 0; }
.rule-form button {
  flex: none; border: 1px solid var(--accent-line); border-radius: var(--r-s);
  background: var(--accent-soft); color: var(--accent);
  font: inherit; font-size: 12px; padding: 0 12px; cursor: pointer;
}
.rule-form button:hover { filter: brightness(.97); }
.rule-form button:disabled { opacity: .45; cursor: not-allowed; }
.rule-tip { margin: 6px 0 0; font-size: 10.5px; color: var(--ink-4); line-height: 1.5; }
.sec-empty { margin: 0; font-size: 11.5px; color: var(--ink-4); line-height: 1.6; }

/* ===== ⑤ 更多：次要信息链接行 ===== */
.more-row {
  width: 100%;
  display: flex; align-items: center; gap: 8px;
  padding: 8px 10px; margin-bottom: 4px;
  background: var(--surface);
  border: 1px solid var(--line-soft);
  border-radius: var(--r-s);
  font: inherit; font-size: 12.5px; color: var(--ink-2);
  cursor: pointer; text-align: left;
  transition: border-color .15s, background .15s;
}
.more-row:hover { border-color: var(--accent-line); background: var(--surface-3); }
.more-row:hover .chev { color: var(--accent); transform: translateX(2px); }
.more-row:focus-visible { outline: 2px solid var(--accent-line); outline-offset: 1px; }
.more-lb { font-weight: 500; }
.more-meta { margin-left: auto; flex: none; font-size: 10.5px; color: var(--ink-4); }
.chev { flex: none; color: var(--ink-4); font-size: 13px; transition: transform .15s, color .15s; }

/* ===== 抽屉内通用 ===== */
.d-card { background: var(--surface); border: 1px solid var(--line); border-radius: var(--r-m); padding: 6px 12px; }
.d-row { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 7px 0; font-size: 12.5px; color: var(--ink-3); border-bottom: 1px dashed var(--line-soft); }
.d-row:last-child { border-bottom: 0; }
.d-row b { font-weight: 600; color: var(--ink-2); text-align: right; }
.d-row b.mono { font-family: var(--mono, ui-monospace, monospace); font-size: 12px; color: var(--accent); }
.d-row.warn b { color: var(--amber, #ca8a04); }
.d-row.col { flex-direction: column; align-items: flex-start; gap: 4px; }
.d-row.col p { margin: 0; font-size: 12px; line-height: 1.6; color: var(--ink-2); }
.d-note { margin: 10px 0 0; font-size: 11.5px; color: var(--ink-4); line-height: 1.6; }

/* 记忆（背景抽屉内） */
.mem { margin-top: 14px; padding-top: 12px; border-top: 1px dashed var(--line-soft); font-size: 12px; color: var(--ink-2); line-height: 1.6; }
.mem .mt { font-size: 10.5px; color: var(--violet); font-weight: 650; margin-bottom: 6px; letter-spacing: .3px; }
.mem-it { margin: 0 0 5px; display: flex; gap: 8px; align-items: flex-start; }
.mem-it span { flex: 1; min-width: 0; }
.mem-it button, .mem-form button, .side-err button {
  flex: none; border: 0; background: transparent; color: var(--accent); cursor: pointer; font: inherit; font-size: 11.5px; padding: 0;
}
.mem-it button:hover, .mem-form button:hover, .side-err button:hover { text-decoration: underline; }
.mem-it button:disabled, .mem-form button:disabled { opacity: .45; cursor: not-allowed; }
.mem-empty { margin: 6px 0 0; color: var(--ink-4); font-size: 11.5px; }
.mem-form { display: flex; gap: 6px; margin-bottom: 6px; }
.mem-form input {
  flex: 1; min-width: 0; border: 1px solid var(--line); border-radius: var(--r-s);
  background: var(--surface); color: var(--ink); font: inherit; font-size: 12px; padding: 5px 8px;
}
.side-err { margin: 6px 0 0; color: var(--danger, #b42318); font-size: 11.5px; line-height: 1.5; }

/* 思考（抽屉内） */
.think-item {
  display: flex; align-items: baseline; gap: 8px;
  padding: 8px 10px; margin-bottom: 5px;
  background: var(--surface);
  border: 1px solid var(--line-soft);
  border-radius: var(--r-s);
  font-size: 12px; line-height: 1.55;
  animation: thinkIn .3s ease both;
}
.think-item.latest { border-color: var(--accent-line); background: linear-gradient(160deg, #F7F5FF, #EFF3FF); }
@keyframes thinkIn { from { opacity: 0; transform: translateX(-6px); } to { opacity: 1; transform: none; } }
.think-tag { flex: none; font-size: 10px; font-weight: 600; padding: 2px 7px; border-radius: 99px; letter-spacing: .02em; }
.think-tag.理解 { background: var(--accent-soft); color: var(--accent); }
.think-tag.意图 { background: #E0E7FF; color: #4338CA; }
.think-tag.数据来源 { background: #DBEAFE; color: #1D4ED8; }
.think-tag.授权 { background: #D1FAE5; color: #047857; }
.think-tag.判断 { background: #FCE7F3; color: #BE185D; }
.think-tag.兜底 { background: #FEF3C7; color: #B45309; }
.think-tag.default { background: var(--surface-3); color: var(--ink-3); }
.think-text { flex: 1; min-width: 0; color: var(--ink-2); word-break: break-word; }
.think-time { flex: none; font-size: 10px; color: var(--ink-4); font-family: var(--mono, ui-monospace, monospace); }
.think-empty {
  background: var(--surface);
  border: 1px dashed var(--line);
  border-radius: var(--r-m);
  padding: 14px 12px;
  text-align: center;
  font-size: 12px; line-height: 1.8; color: var(--ink-4);
}
.rule-it.lg { padding: 9px 12px; }

/* ===== 深色模式微调 ===== */
html[data-mode="dark"] .side { background: #13151A; }
html[data-mode="dark"] .st.run,
html[data-mode="dark"] .op-step.running,
html[data-mode="dark"] .think-item.latest {
  background: color-mix(in srgb, var(--accent) 12%, #1a1d24);
  border-color: color-mix(in srgb, var(--accent) 30%, transparent);
}
html[data-mode="dark"] .more-row:hover { background: #1a1d24; }
html[data-mode="dark"] .rule-form button { background: color-mix(in srgb, var(--accent) 14%, #1a1d24); }
</style>
